import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, STATUS_LABEL, hr, hrDateTime, statusBadgeVariant } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";
import { CalendarDays, X, HomeIcon, ArrowDownWideNarrow, ArrowUpWideNarrow } from "lucide-react";
import { toast } from "sonner";
import { RequestStatusStepper } from "../components/RequestStatusStepper";

export default function MyRequests() {
  const { user } = useAuth();
  const [vacation, setVacation] = useState([]);
  const [wfh, setWfh] = useState([]);
  const [sortDir, setSortDir] = useState("desc"); // desc = newest first

  const load = useCallback(async () => {
    const [v, w] = await Promise.all([
      api.get("/vacation/requests").then(r => r.data.filter(x => x.employee_email === user.email)),
      api.get("/wfh/requests").then(r => r.data.filter(x => x.employee_email === user.email)),
    ]);
    setVacation(v);
    setWfh(w);
  }, [user.email]);
  useEffect(() => { load(); }, [load]);

  const sortByDate = useCallback((rows) => {
    return [...rows].sort((a, b) => {
      const da = new Date(a.created_at || 0).getTime();
      const db = new Date(b.created_at || 0).getTime();
      return sortDir === "desc" ? db - da : da - db;
    });
  }, [sortDir]);

  const sortedVacation = useMemo(() => sortByDate(vacation), [vacation, sortByDate]);
  const sortedWfh = useMemo(() => sortByDate(wfh), [wfh, sortByDate]);

  const cancelVacation = async (id) => {
    try { await api.delete(`/vacation/requests/${id}`); toast.success("Zahtjev otkazan"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
  };
  const cancelWfh = async (id) => {
    try { await api.delete(`/wfh/requests/${id}`); toast.success("Zahtjev otkazan"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Greška"); }
  };

  const renderVacation = (r) => {
    const canCancel = r.status === "pending" || r.status === "approved" || r.status === "rejected";
    return (
      <Card key={r.id} className="hover:border-accent transition-colors mb-3" data-testid={`request-${r.id}`}>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <Link to={`/zahtjev/${r.id}`} className="flex items-center gap-4 min-w-0 flex-1">
              <CalendarDays className="w-6 h-6 text-primary shrink-0" />
              <div className="min-w-0">
                <div className="font-medium truncate">{hr(r.collective_start)} – {hr(r.collective_end)} {r.additional_periods?.length > 0 && `+ ${r.additional_periods.length} dodatnih`}</div>
                <div className="text-xs text-muted-foreground">Ukupno {r.total_days} radnih dana · predano {hrDateTime(r.created_at)}</div>
              </div>
            </Link>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant={statusBadgeVariant(r.status)}>{STATUS_LABEL[r.status]}</Badge>
              {canCancel && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" data-testid={`cancel-${r.id}`} title="Otkaži zahtjev"><X className="w-4 h-4"/></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Otkazati zahtjev?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Zahtjev za period <b>{hr(r.collective_start)} – {hr(r.collective_end)}</b> bit će trajno obrisan. Ova akcija se ne može poništiti.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Odustani</AlertDialogCancel>
                      <AlertDialogAction onClick={() => cancelVacation(r.id)} data-testid={`confirm-cancel-${r.id}`}>Otkaži zahtjev</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
          <RequestStatusStepper request={r} className="pt-1" />
        </CardContent>
      </Card>
    );
  };

  const renderWfh = (r) => {
    const canCancel = r.status === "pending" || r.status === "approved" || r.status === "rejected";
    return (
      <Card key={r.id} className="hover:border-accent transition-colors mb-3" data-testid={`wfh-request-${r.id}`}>
        <CardContent className="p-5 space-y-3">
          <div className="flex items-center justify-between gap-4">
            <Link to={`/wfh/zahtjev/${r.id}`} className="flex items-center gap-4 min-w-0 flex-1">
              <HomeIcon className="w-6 h-6 text-accent shrink-0" />
              <div className="min-w-0">
                <div className="font-medium truncate">{hr(r.start_date)} – {hr(r.end_date)}</div>
                <div className="text-xs text-muted-foreground truncate">
                  {r.broj_mobitela ? `Mob. ${r.broj_mobitela} · ` : (r.lokacija ? `${r.lokacija} · ` : "")}
                  predano {hrDateTime(r.created_at)}
                </div>
              </div>
            </Link>
            <div className="flex items-center gap-2 shrink-0">
              <Badge variant={statusBadgeVariant(r.status)}>{STATUS_LABEL[r.status]}</Badge>
              {canCancel && (
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" data-testid={`wfh-cancel-${r.id}`} title="Otkaži zahtjev"><X className="w-4 h-4"/></Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Otkazati zahtjev?</AlertDialogTitle>
                      <AlertDialogDescription>
                        Zahtjev za rad od kuće <b>{hr(r.start_date)} – {hr(r.end_date)}</b> bit će trajno obrisan.
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Odustani</AlertDialogCancel>
                      <AlertDialogAction onClick={() => cancelWfh(r.id)} data-testid={`confirm-wfh-cancel-${r.id}`}>Otkaži zahtjev</AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              )}
            </div>
          </div>
          <RequestStatusStepper request={r} className="pt-1" />
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="space-y-4" data-testid="my-requests">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <h1 className="serif text-4xl font-semibold">Moji zahtjevi</h1>
        <Button variant="outline" size="sm"
          onClick={() => setSortDir(d => d === "desc" ? "asc" : "desc")}
          data-testid="my-sort-toggle"
          title={`Sortirano: ${sortDir === "desc" ? "najnoviji prvo" : "najstariji prvo"}`}>
          {sortDir === "desc"
            ? <><ArrowDownWideNarrow className="w-4 h-4 mr-1.5"/> Najnoviji prvo</>
            : <><ArrowUpWideNarrow className="w-4 h-4 mr-1.5"/> Najstariji prvo</>}
        </Button>
      </div>
      <Tabs defaultValue="vacation">
        <TabsList>
          <TabsTrigger value="vacation" data-testid="tab-my-vacation">Godišnji odmor ({vacation.length})</TabsTrigger>
          <TabsTrigger value="wfh" data-testid="tab-my-wfh">Rad od kuće ({wfh.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="vacation" className="mt-4">
          {sortedVacation.length === 0
            ? <Card><CardContent className="p-12 text-center text-muted-foreground">Još nemate predanih zahtjeva za godišnji odmor.</CardContent></Card>
            : sortedVacation.map(renderVacation)}
        </TabsContent>
        <TabsContent value="wfh" className="mt-4">
          {sortedWfh.length === 0
            ? <Card><CardContent className="p-12 text-center text-muted-foreground">Još nemate predanih zahtjeva za rad od kuće.</CardContent></Card>
            : sortedWfh.map(renderWfh)}
        </TabsContent>
      </Tabs>
    </div>
  );
}
